import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { FC, useEffect, useRef, useState } from "react";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { PasswordConfig } from "../../models/SecurityPolicyPasswordConfig";

interface PasswordPolicyHintProps {
    passwordConfig: PasswordConfig;
}

const PANEL_WIDTH = 224; // px — matches w-56

const toConciseRuleText = (rule: string): string => {
    return rule
        .replace(/^new password must\s+/i, "")
        .replace(/^password must\s+/i, "")
        .replace(/^(contain|contains|be)\s+/i, "")
        .replace(/[.]+$/, "")
        .trim();
};

/**
 * Displays a small info button next to the new-password label.
 * Hovering it shows a fixed-position panel listing the active password
 * policy rules, so users know exactly what is required before they start typing.
 *
 * Fixed positioning is used so the panel is never clipped by a narrow ancestor.
 */
const PasswordPolicyHint: FC<PasswordPolicyHintProps> = ({ passwordConfig }) => {
    const { translate } = useLocale();
    const [open, setOpen] = useState(false);
    const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
    const buttonRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const canHover = typeof window !== "undefined" && window.matchMedia("(hover: hover)").matches;

    const rules: string[] = [];

    if (passwordConfig.enforceUppercase) {
        rules.push(translate.byKey(TranslationKeys.passwordUppercase));
    }
    if (passwordConfig.enforceLowercase) {
        rules.push(translate.byKey(TranslationKeys.passwordLowercase));
    }
    if (passwordConfig.enforceDigits) {
        rules.push(translate.byKey(TranslationKeys.passwordDigit));
    }
    if (passwordConfig.enforceSpecialCharacters) {
        rules.push(translate.byKey(TranslationKeys.passwordSpecialCharacter));
    }
    if (passwordConfig.enforcePasswordMinLength && passwordConfig.passwordMinLength > 0) {
        rules.push(translate.byKeyFormatted(TranslationKeys.passwordLength, [passwordConfig.passwordMinLength]));
    }

    const displayRules = rules.map(rule => toConciseRuleText(rule));

    useEffect(() => {
        if (!open || !buttonRef.current) return;

        const rect = buttonRef.current.getBoundingClientRect();
        const top = rect.bottom + 4;

        // Prefer aligning left edge with button; shift left if it would overflow viewport.
        let left = rect.left;
        if (left + PANEL_WIDTH > window.innerWidth - 8) {
            left = window.innerWidth - PANEL_WIDTH - 8;
        }

        setPanelStyle({ position: 'fixed', top, left, width: PANEL_WIDTH });
    }, [open]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const handlePointerDown = (event: MouseEvent | TouchEvent) => {
            const targetNode = event.target as Node;
            const clickedButton = buttonRef.current?.contains(targetNode);
            const clickedPanel = panelRef.current?.contains(targetNode);

            if (!clickedButton && !clickedPanel) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("touchstart", handlePointerDown);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("touchstart", handlePointerDown);
        };
    }, [open]);

    return (
        <span className="inline-flex items-center ml-1">
            <button
                ref={buttonRef}
                type="button"
                tabIndex={-1}
                aria-label={translate.byKey(TranslationKeys.passwordPolicyHint)}
                className="text-gray-400 hover:text-portal-blue-primary focus:outline-none"
                onMouseEnter={() => canHover && setOpen(true)}
                onMouseLeave={() => canHover && setOpen(false)}
                onClick={() => !canHover && setOpen(o => !o)}>
                <FontAwesomeIcon icon={faInfoCircle} className="text-xs" />
            </button>
            {open && (
                <div
                    ref={panelRef}
                    style={panelStyle}
                    onMouseEnter={() => canHover && setOpen(true)}
                    onMouseLeave={() => canHover && setOpen(false)}
                    className="z-50 bg-white border border-gray-200 shadow-md rounded p-3 text-xs text-gray-700 whitespace-normal break-words">
                    <p className="font-semibold mb-1">{translate.byKey(TranslationKeys.passwordPolicyHint)}</p>
                    {rules.length === 0
                        ? <p>{translate.byKey(TranslationKeys.passwordPolicyNoRules)}</p>
                        : <ul className="list-disc list-inside space-y-0.5">
                            {displayRules.map((rule, i) => <li key={i}>{rule}</li>)}
                          </ul>
                    }
                </div>
            )}
        </span>
    );
};

export default PasswordPolicyHint;
